import React, { useRef, useCallback, useEffect, useState } from 'react';
import {
  matchPath, Outlet, useNavigate
} from 'react-router-dom';
import HeaderContainer from './components/HeaderContainer/HeaderContainer';
import NavigationBarContainer from './components/NavigationBarContainer/NavigationBarContainer';
import {
  ProjectContentUI,
  ProjectMainContainerUI,
  ProjectWrapUI,
} from './ui/ProjectPageUI';
import RouteNameConstants from '../../constants/route-name.constants';
import {
  useActiveProjectCalendarsSelector,
  useActiveProjectSelector, useCalendarOpenSelector, useCpmCompleteSelector, useCpmMapSelector,
  useIsLoadedActiveProjectSelector, useLedgerOpenSelector, useProjectViewSelector, useTeamOpenSelector,
} from '../../store/selectors/project.selectors';
import { SearchBar } from './components/SearchBar/SearchBar';
import { HeaderLedger } from './components/HeaderContainer/components/HeaderLedger';
import { HeaderMember } from './components/HeaderContainer/components/HeaderMember';
import { useDispatch } from 'react-redux';
import AuthorizationThunk from '../../store/thunk/authorization.thunk';
import { useTypedSelector } from '../../store/selectors/selectors.utils';
import classNames from 'classnames';
import HeaderCalendar from './components/HeaderContainer/components/HeaderCalendar/HeaderCalendar';
import { subsManager } from '../../store/middleware/subs-manager/subs-manager';
import CustomerRequestModal from "./components/CustomerRequest/CustomerRequestModal";
import {Chat} from "./components/FlowChat/FlowChat";
import * as ledgerActions from "../../store/actions/ledger.actions";
import * as projectActions from "../../store/actions/project.actions";
import {useAllTaskListSelector, useSessionChangeCountSelector} from "../../store/selectors/task/task.selectors";
import {useSearchParamatersSelector} from "../../store/selectors/search.selectors";
import filterTaskList from "../../utils/task-filtering";
import * as taskActions from "../../store/actions/task.actions";

const ProjectPage = () => {
  const activeProject = useActiveProjectSelector();
  const cpmComplete = useCpmCompleteSelector();
  const isLoadedActiveProject = useIsLoadedActiveProjectSelector();
  const isOpenedLedger = useLedgerOpenSelector();
  const isOpenedCalendar = useCalendarOpenSelector();
  const isOpenedMember = useTeamOpenSelector()
  const [isOpenedProfile, setProfileIsOpen] = useState(false);
  const [isLarge, setLargeMode] = useState<boolean>(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const history = useNavigate();
  const globalView = useProjectViewSelector();
  const openPredecessors = useTypedSelector(
    (state) => state.task.predecessors.open
  );
  const openSuccessors = useTypedSelector(
    (state) => state.task.successors.open
  );
  const activeTask = useTypedSelector((state) => state.task.activeTask);
  const [previousActiveTask, setPreviousActiveTask] = useState<any>(null);
  const dispatch = useDispatch();
  const isMountedRef = useRef(false);
  const searchParams = useSearchParamatersSelector();
  const allTaskList = useAllTaskListSelector();
  const cpmMap = useCpmMapSelector();
  const calendarsMap = useActiveProjectCalendarsSelector();
  const sessionChangeCount = useSessionChangeCountSelector();

  useEffect(() => {
    if (!activeProject) {
      dispatch(taskActions.Actions.setFilteredTaskList([]));
    } else {
      dispatch(taskActions.Actions.setFilteredTaskList(filterTaskList(allTaskList, searchParams, cpmMap, calendarsMap)));
    }
  }, [activeProject, searchParams, allTaskList, sessionChangeCount]);

  const openSpecificAdditional = useCallback(
    (type: 'ledger' | 'member' | 'profile' | 'calendar', open: boolean) => {
      if (type === 'ledger') {
        dispatch(projectActions.Actions.setLedgerOpen(open));
      } else if (type === 'member') {
        dispatch(projectActions.Actions.setTeamOpen(open));
      } else if (type === 'calendar') {
        dispatch(projectActions.Actions.setCalendarOpen(open));
      } else {
        setProfileIsOpen(prevState => !prevState);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpenedLedger && activeTask && activeTask.task_code !== previousActiveTask) {
        subsManager.removeSubscription('ledger-list');
        subsManager.subscribeLedgerList(activeTask.task_code);
        setPreviousActiveTask(activeTask.task_code);
    } else if (!isOpenedLedger || !activeTask) {
        dispatch(ledgerActions.Actions.setLedgerList([]));
        setPreviousActiveTask(null);
        subsManager.removeSubscription('ledger-list');
    }
  }, [isOpenedLedger, activeTask]);

  const handleResizeEvent = () => {
    setTimeout(() => {
      const flowPageDocument = document.getElementById('flow-page-id');
      if (flowPageDocument) {
        // add event to trigger if width of document changes
        dispatch(projectActions.Actions.setViewWidth(flowPageDocument.clientWidth));
      }
    }, 500);
  }

  useEffect(() => {
    handleResizeEvent();
  }, [isOpenedLedger, isOpenedMember, isOpenedCalendar]);

  //
  const logOut = useCallback(
    (evt: any) => {
      evt.preventDefault();
      if (isMountedRef.current) {
        // @ts-ignore
        dispatch(AuthorizationThunk.logOut());
      }
    },
    [dispatch, isMountedRef]
  );

  useEffect(() => {
    isMountedRef.current = true;

    const matchPathname = matchPath( {path: '/:projectId/flow/:task_id'}, window.location.pathname);
    const params: any = matchPathname
      ? matchPathname.params
      : {};
    const activeProjectId = params.project_id || localStorage.getItem('projectId');

    if (activeProject && isLoadedActiveProject && !activeProject.disabled) {
      history(`/${activeProjectId}/${RouteNameConstants.FLOW}${params.task_id ? `/${params.task_id}` : ''}`);
    }

    if (!activeProject && !activeProjectId) {
      history(`/${RouteNameConstants.CHOOSE_PROJECT}`);
    }

    if (!activeProject) {
      dispatch(projectActions.Actions.setLedgerOpen(false));
      dispatch(projectActions.Actions.setTeamOpen(false));
      dispatch(projectActions.Actions.setCalendarOpen(false));
    }
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, isLoadedActiveProject]);

  return (
    <ProjectMainContainerUI
      style={{ overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', height: '100vh' }}
    >
      <HeaderContainer
        logout={logOut}
        openSpecificAdditional={openSpecificAdditional}
        isOpenedCalendar={isOpenedCalendar}
        isOpenedLedger={isOpenedLedger}
        isOpenedMember={isOpenedMember}
        isOpenedProfile={isOpenedProfile}
        isLarge={isLarge}
        setLargeMode={setLargeMode}
        setRequestModalOpen={setRequestModalOpen}
      />
      <SearchBar disabled={openPredecessors || openSuccessors}
        openSpecificAdditional={openSpecificAdditional}
        isOpenedCalendar={isOpenedCalendar}
        isOpenedLedger={isOpenedLedger}
        isOpenedMember={isOpenedMember}
        isOpenedProfile={isOpenedProfile}
        isLarge={isLarge}
        setLargeMode={setLargeMode}
      />
      {requestModalOpen && <CustomerRequestModal onClose={() => setRequestModalOpen(false)} />}
      <ProjectWrapUI style={{ flex: 1 }}>
        <NavigationBarContainer setLargeMode={setLargeMode} isLarge={isLarge} />
        <ProjectContentUI
          className="project-content"
        >
          <Outlet/>
        </ProjectContentUI>
        <div style={{ display: 'flex'}}>
          <div
            className={classNames('header-additional-content-wrap', {
              open: isLarge ? !isLarge : isOpenedCalendar,
            })}
          >
            <div className="header-additional-content">
              <HeaderCalendar />
            </div>
          </div>
          <div
            className={classNames('header-additional-content-wrap', {
              open: isLarge ? !isLarge : isOpenedLedger,
            })}
          >
            <div className="header-additional-content">
              <HeaderLedger />
            </div>
          </div>
          <div
            className={classNames('header-additional-content-wrap', {
              open: isLarge ? !isLarge : isOpenedMember,
            })}
          >
            <div className="header-additional-content">
              <HeaderMember />
            </div>
          </div>
        </div>
      </ProjectWrapUI>
      {/*{cpmComplete && activeProject && !chatOpen &&*/}
      {/*  <div className="open-chat" onClick={() => setChatOpen(true)}>*/}
      {/*    <FontAwesomeIcon icon={faMessageLines} />*/}
      {/*  </div>*/}
      {/*}*/}
      {cpmComplete && activeProject && chatOpen &&
        <div className="chat-container">
          <Chat setShowChat={setChatOpen} />
        </div>
      }
    </ProjectMainContainerUI>
  )
};

export default ProjectPage;
